import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TIER_ORDER, TIER_LABELS, TIER_BADGE_CLASSES, formatINR } from "@/lib/format";

export function HotelsPricingMatrix({ tour }) {
  const tiers = TIER_ORDER.filter((t) => tour.hotel_tiers?.[t]);
  const useCards = useMediaQuery("(max-width: 767px)");

  if (tiers.length === 0) {
    return <p className="text-sm text-slate-500">Hotel & pricing details will be shared upon enquiry.</p>;
  }

  if (useCards) return <div data-testid="hotels-pricing-matrix" className="space-y-4">
    <p data-testid="mobile-tier-help" className="text-base leading-relaxed text-muted-foreground">Compare hotel options. Prices below are per person for your group size.</p>
    {tiers.map((tier) => <Card key={tier} data-testid={`hotel-tier-card-${tier}`} className="min-w-0 overflow-hidden shadow-none">
      <div className="border-b bg-muted/40 p-4"><Badge data-testid={`tier-badge-${tier}`} className={TIER_BADGE_CLASSES[tier]}>{TIER_LABELS[tier]}</Badge></div>
      <div className="space-y-4 p-4">
        <dl className="space-y-3">
          {[2, 4].map((pax) => <div key={pax} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1" data-testid={`hotel-tier-${tier}-price-${pax}`}>
            <dt className="text-sm text-muted-foreground">{pax} adults</dt>
            <dd className="font-semibold text-foreground">{formatINR(tour.hotel_tiers[tier][`cost_${pax}_pax`])}{tour.hotel_tiers[tier][`cost_${pax}_pax`] ? <span className="text-xs font-normal text-muted-foreground"> / person</span> : null}</dd>
          </div>)}
        </dl>
        <div className="border-t pt-4"><h3 className="mb-2 font-sans text-base font-semibold">Hotels</h3>
          {tour.hotel_tiers[tier].hotel_names?.length ? <ul className="list-disc space-y-2 break-words pl-4 text-sm leading-relaxed text-muted-foreground">{tour.hotel_tiers[tier].hotel_names.map((hotel, index) => <li key={index} data-testid={`hotel-tier-${tier}-hotel-${index}`}>{hotel}</li>)}</ul> : <p className="text-sm text-muted-foreground">Shared upon enquiry</p>}
        </div>
      </div>
    </Card>)}
  </div>;

  return (
    <div data-testid="hotels-pricing-matrix" className="overflow-x-auto rounded-xl border">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="w-40 min-w-[10rem] font-display">Comfort Level</TableHead>
            {tiers.map((tier) => (
              <TableHead key={tier} className="min-w-[13rem] text-left">
                <Badge data-testid="tier-badge" className={TIER_BADGE_CLASSES[tier]}>{TIER_LABELS[tier]}</Badge>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="align-top font-medium text-slate-700">Hotels</TableCell>
            {tiers.map((tier) => (
              <TableCell key={tier} className="align-top">
                {tour.hotel_tiers[tier].hotel_names?.length > 0 ? (
                  <ul className="space-y-1 text-sm text-slate-600">
                    {tour.hotel_tiers[tier].hotel_names.map((h, i) => (
                      <li key={i}>{h}</li>
                    ))}
                  </ul>
                ) : (
                  <span data-testid="matrix-price-on-request" className="text-sm text-slate-400">Shared upon enquiry</span>
                )}
              </TableCell>
            ))}
          </TableRow>
          <TableRow className="bg-slate-50/60">
            <TableCell className="font-medium text-slate-700">Cost for 2 Adults</TableCell>
            {tiers.map((tier) => {
              const val = tour.hotel_tiers[tier].cost_2_pax;
              return (
                <TableCell key={tier} className="font-semibold text-slate-900">
                  {val ? (
                    <>{formatINR(val)} <span className="text-xs font-normal text-slate-500">/ person</span></>
                  ) : (
                    <Badge data-testid="matrix-price-on-request" variant="outline" className="text-slate-500">Price on Request</Badge>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
          <TableRow>
            <TableCell className="font-medium text-slate-700">Cost for 4 Adults</TableCell>
            {tiers.map((tier) => {
              const val = tour.hotel_tiers[tier].cost_4_pax;
              return (
                <TableCell key={tier} className="font-semibold text-slate-900">
                  {val ? (
                    <>{formatINR(val)} <span className="text-xs font-normal text-slate-500">/ person</span></>
                  ) : (
                    <Badge data-testid="matrix-price-on-request" variant="outline" className="text-slate-500">Price on Request</Badge>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
